import React, { useEffect, useState } from 'react'
import { Button, Tabs, Modal, Input, Table, Spin, Progress, notification } from 'antd'
import { useStoreActions, useStoreState } from 'easy-peasy'
import { InfoCircleOutlined } from '@ant-design/icons'
import { SideBar } from '@styled-icons/remix-line/SideBar'
import { Play as PlayIcon } from '@styled-icons/bootstrap/Play'

const GGSheet = ({ onSelectScreen }: { onSelectScreen?: (v: string) => void }) => {
  const { google_sheet_url } = useStoreState((state: any) => state.customerSegments)
  const { setState } = useStoreActions((action: any) => action.customerSegments)
  const [activeTab, setActiveTab] = React.useState('result')
  const [loading, setLoading] = useState(false)
  const [sheetData, setSheetData] = useState<any[]>([])
  const [columns, setColumns] = useState<any[]>([])
  const [error, setError] = useState<string>('')
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [showProgress, setShowProgress] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => `Total ${total} items`,
  })

  const processDataInChunks = (rows: string[][], headers: string[]) => {
    const CHUNK_SIZE = 1000;
    const totalRows = rows.length;
    let processedRows = 0;
    let allData: any[] = [];
    
    return new Promise<any[]>((resolve) => {
      function processChunk() {
        const chunkEnd = Math.min(processedRows + CHUNK_SIZE, totalRows);
        
        for (let i = processedRows; i < chunkEnd; i++) {
          const row = rows[i];
          const rowData: any = { key: i - 1 }; // Adjust for header row
          headers.forEach((header, j) => {
            rowData[header] = row[j];
          });
          allData.push(rowData);
        }
        
        processedRows = chunkEnd;
        const progress = Math.floor((processedRows / totalRows) * 100);
        setLoadingProgress(progress);
        
        if (processedRows < totalRows) {
          setTimeout(processChunk, 0); // Use setTimeout to prevent UI blocking
        } else {
          resolve(allData);
        }
      }
      
      processChunk();
    });
  };

  const fetchSheetData = async () => {
    if (!google_sheet_url) return

    setLoading(true)
    setError('')
    setShowProgress(true)
    setLoadingProgress(0)
    try {
      // Extract the sheet ID from the URL - handle both edit and view formats
      const sheetIdMatch = google_sheet_url.match(/\/d\/([a-zA-Z0-9-_]+)/);
      const sheetId = sheetIdMatch?.[1];
      
      if (!sheetId) {
        throw new Error('Invalid Google Sheet URL. Expected format: https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/...')
      }
      
      // Extract gid parameter if available (for multi-sheet documents)
      let gid = '0';
      const gidMatch = google_sheet_url.match(/[#&]gid=([0-9]+)/);
      if (gidMatch && gidMatch[1]) {
        gid = gidMatch[1];
      }

      // Use the export=csv format which is more reliable for larger datasets
      const csvUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${gid}`;
      
      notification.info({
        message: 'Fetching data',
        description: 'Starting to download sheet data. This may take a while for large sheets.',
        duration: 3
      });
      
      console.log('Fetching from URL:', csvUrl);
      
      const response = await fetch(csvUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}. Make sure the sheet is shared publicly with "Anyone with the link can view" access.`)
      }
      
      notification.info({
        message: 'Processing data',
        description: 'Sheet data downloaded. Now processing the data...',
        duration: 3
      });
      
      const csvText = await response.text()

      // Log the first few lines to debug
      console.log('CSV first 200 chars:', csvText.substring(0, 200));
      
      // Handle empty response
      if (!csvText || csvText.trim() === '') {
        throw new Error('The sheet returned empty data. Please check if the sheet has data and proper permissions.')
      }

      // Try to detect CSV format and parse
      let rows: string[][] = [];
      try {
        // Check if the response is an HTML error page instead of CSV
        if (csvText.includes('<!DOCTYPE html>') || csvText.includes('<html>')) {
          throw new Error('Received HTML instead of CSV data. The sheet might require authentication or have restricted access.');
        }
        
        // Parse CSV data and remove quotes
        rows = csvText.split('\n').map(row => {
          // Handle both comma and tab delimiters
          const delimiter = row.includes(',') ? ',' : '\t';
          return row.split(delimiter).map(cell => cell.replace(/^"|"$/g, '').trim())
        });
        
        // Log parsed rows for debugging
        console.log('First row:', rows[0]);
        console.log('Total rows:', rows.length);
      } catch (error: any) {
        console.error('CSV parsing error:', error);
        throw new Error(`Failed to parse sheet data: ${error.message || 'Unknown parsing error'}`);
      }
      
      if (rows.length > 0) {
        const headers = rows[0];
        
        console.log('Headers found:', headers);
        
        // Check if customer_id column exists (handle both with and without space)
        const customerIdColumn = headers.find(header => 
          header === 'customer_id'
        );
        
        if (!customerIdColumn) {
          setError('Google Sheet must contain a column named "customer_id". This column is required for identifying customers.')
          setSheetData([])
          setColumns([])
          return
        }

        // Process data in chunks to prevent UI freezing
        const data = await processDataInChunks(rows.slice(1), headers);

        setColumns(headers.map((header: string) => ({
          title: header,
          dataIndex: header,
          key: header,
          ellipsis: true,
          width: 200,
        })))

        setSheetData(data)
        setPagination(prev => ({
          ...prev,
          total: data.length
        }))
        
        notification.success({
          message: 'Data loaded successfully',
          description: `Processed ${data.length} rows of data`,
          duration: 3
        });
      }
    } catch (error) {
      console.error('Error fetching sheet data:', error)
      setError('Failed to fetch Google Sheet data. Please make sure the sheet is publicly accessible.')
      Modal.error({
        title: 'Error',
        content: 'Failed to fetch Google Sheet data. Please make sure the sheet is publicly accessible.',
      })
    } finally {
      setLoading(false)
      setShowProgress(false)
    }
  }

  const handleTableChange = (newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      ...newPagination
    }))
  }

  const handleRun = () => {
    fetchSheetData()
  }

  useEffect(() => {
    if (google_sheet_url) {
      fetchSheetData()
    }
  }, [google_sheet_url])

  return (
    <div className="w-full px-8 py-3">
      {onSelectScreen && (
        <Button
          className="bg-white font-semibold px-2 flex items-center"
          onClick={() => {
            onSelectScreen('BLANK')
          }}
        >
          <SideBar style={{ width: 18, height: 18, marginRight: 10 }} />
          Use condition
        </Button>
      )}
      <div className="mt-3 grid bg-white w-full">
        <div className="col-span-3">
          <div className="p-2 border-b w-full flex justify-end">
            <Button
              style={{ width: 40, height: 40 }}
              className="font-semibold p-0 mr-2"
              onClick={() =>
                Modal.info({
                  okText: 'Done',
                  style: { width: 500 },
                  className: 'custom-sql-guideline',
                  title: 'Query Guideline',
                  content: (
                    <div>
                      <p>
                        - Nếu segment sử dụng để gửi <span>web push</span> thì cần return column{' '}
                        <span>customer_id</span>,<span>browser_token</span>.
                      </p>
                      <p>
                        - Nếu segment sử dụng để gửi <span>app push</span> thì cần return column{' '}
                        <span>customer_id</span>,<span>app_user_id</span>.
                      </p>
                      <p>
                        - Nếu segment sử dụng để gửi <span>email</span> thì cần return column{' '}
                        <span>customer_id</span>, <span>email</span>.
                      </p>
                      <p>- Recommended Table: mapping từ juno:kariba_production.audience_360</p>
                      <p>
                        - Các google sheet mà không return kết quả là do google sheet không hợp lệ hoặc
                        chưa return đủ các column yêu cầu. Column{' '}
                        <span>customer_id là yêu cầu bắt buộc đối với google sheet</span>
                      </p>
                    </div>
                  ),
                  onOk() {},
                })
              }
            >
              <InfoCircleOutlined style={{ fontSize: 16 }} />
            </Button>
            <Button
              loading={loading}
              disabled={!google_sheet_url || loading}
              style={{ height: 40 }}
              className="font-semibold p-2"
              onClick={handleRun}
            >
              <PlayIcon className="mr-2" style={{ width: 22, height: 22 }} />
              Run
            </Button>
          </div>
          <div className="p-4 border-b w-full">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Source</label>
              <div className="flex items-center gap-2">
                <Input 
                  placeholder="Enter google sheet url" 
                  value={google_sheet_url} 
                  onChange={(e) => setState({ key: 'google_sheet_url', value: e.target.value })} 
                  disabled={loading}
                />
              </div>
              {error && (
                <div className="text-red-500 text-sm">{error}</div>
              )}
            </div>
          </div>

          <div style={{ minHeight: 300 }} className="mt-2 px-4">
            {showProgress && (
              <div className="my-4">
                <Progress percent={loadingProgress} status="active" />
                <p className="text-center text-gray-500">
                  {loadingProgress < 100 
                    ? `Processing data: ${loadingProgress}% complete` 
                    : 'Finalizing data preparation...'
                  }
                </p>
              </div>
            )}
            <Tabs
              defaultActiveKey="result"
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key)}
            >
              <Tabs.TabPane tab="Result" key="result">
                {loading && !showProgress ? (
                  <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    dataSource={sheetData}
                    scroll={{ x: true }}
                    pagination={pagination}
                    onChange={handleTableChange}
                    size="middle"
                    bordered
                    className='mt-2'
                  />
                )}
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GGSheet
